import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUser, FaUserPlus, FaUpload, FaFileCsv, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { TrainingRequest, TrainingType } from '../types';
import { CloseModal } from '../../sites/closemodal';
import { SearchableSelect } from '../../../utils/select-with-search';
import api from '../../../libs/axios';

interface RequestFormData {
  candidate_name: string;
  candidate_phone: string;
  candidate_location: string;
  candidate_designation: string;
  designation_id: number | null;
  association_type: string;
  vendor_id: string;
  training_type_id: string;
  project_id: string;
  remarks: string;
  deduction_applicable: boolean;
  deduction_period: string;
  deduction_remarks: string;
}

interface UserSearchResult {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone?: string;
  phone_number?: string;
  employee_code?: string;
  emp_code?: string;
  designation?: { id: number; name: string };
  location?: string;
}

interface BulkAnalysisResult {
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  available_columns: string[];
  required_fields: Array<{
    field_name: string;
    display_name: string;
    required: boolean;
    type: string;
    example?: string;
  }>;
  validation_errors: Array<{
    row_number: number;
    column: string;
    message: string;
  }>;
  sample_data: Record<string, any>[];
  mode_summary?: {
    existing: number;
    new: number;
  };
}

interface BulkImportResult {
  total_rows: number;
  success_count: number;
  failed_count: number;
  created_requests: Array<{
    id: number;
    request_id: string;
    candidate_name: string;
    mode: string;
  }>;
  failed_rows: Array<{
    row_number: number;
    reason: string;
  }>;
}

interface AddRequestOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  onBulkImportComplete?: () => void | Promise<void>;
  trainingTypes: TrainingType[];
  loading?: boolean;
  duplicateFrom?: TrainingRequest | null;
}

const EMPTY_FORM: RequestFormData = {
  candidate_name: '',
  candidate_phone: '',
  candidate_location: '',
  candidate_designation: '',
  designation_id: null,
  association_type: 'onrole',
  vendor_id: '',
  training_type_id: '',
  project_id: '',
  remarks: '',
  deduction_applicable: true,
  deduction_period: 'leaving_time',
  deduction_remarks: '',
};

const VALID_BULK_FILE_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const AddRequestOffcanvas: React.FC<AddRequestOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onBulkImportComplete,
  trainingTypes,
  loading = false,
  duplicateFrom = null,
}) => {
  const [mode, setMode] = useState<'existing' | 'new' | 'bulk'>('existing');
  const [formData, setFormData] = useState<RequestFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof RequestFormData, string>>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [requestorDocFiles, setRequestorDocFiles] = useState<File[]>([]);

  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<UserSearchResult[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);

  const [designationOptions, setDesignationOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedDesignation, setSelectedDesignation] = useState<{ value: number; label: string } | null>(null);
  const [designationLoading, setDesignationLoading] = useState(false);

  const [vendorOptions, setVendorOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<{ value: number; label: string } | null>(null);
  const [vendorLoading, setVendorLoading] = useState(false);

  // Only the requester's own assigned projects can be picked; HR may move the
  // request to another project later, at HR verification.
  const [projectOptions, setProjectOptions] = useState<{ id: number; label: string; short_name: string }[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkAnalysis, setBulkAnalysis] = useState<BulkAnalysisResult | null>(null);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResult | null>(null);
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (duplicateFrom) {
        setMode(duplicateFrom.user_id ? 'existing' : 'new');
        setFormData({
          candidate_name: duplicateFrom.candidate_name || '',
          candidate_phone: duplicateFrom.candidate_phone || '',
          candidate_location: duplicateFrom.candidate_location || '',
          candidate_designation: duplicateFrom.candidate_designation || '',
          designation_id: duplicateFrom.designation_id ?? null,
          association_type: duplicateFrom.association_type || 'onrole',
          vendor_id: duplicateFrom.vendor_id ? String(duplicateFrom.vendor_id) : '',
          training_type_id: '',
          project_id: (duplicateFrom as any).project_id ? String((duplicateFrom as any).project_id) : '',
          remarks: (duplicateFrom as any).remarks || '',
          deduction_applicable: duplicateFrom.deduction_applicable,
          deduction_period: duplicateFrom.deduction_period || 'leaving_time',
          deduction_remarks: duplicateFrom.deduction_remarks || '',
        });
        setSelectedUser(duplicateFrom.linked_user ? {
          id: duplicateFrom.linked_user.id,
          first_name: duplicateFrom.linked_user.first_name,
          last_name: duplicateFrom.linked_user.last_name,
          username: (duplicateFrom.linked_user as any).username || '',
          email: duplicateFrom.linked_user.email || '',
          phone_number: duplicateFrom.candidate_phone,
          employee_code: (duplicateFrom.linked_user as any).employee_code,
          designation: duplicateFrom.designation_id ? { id: duplicateFrom.designation_id, name: duplicateFrom.candidate_designation } : undefined,
          location: duplicateFrom.candidate_location,
        } : null);
        setSelectedDesignation(
          duplicateFrom.designation_id
            ? { value: duplicateFrom.designation_id, label: duplicateFrom.candidate_designation }
            : null,
        );
      } else {
        setMode('existing');
        setFormData(EMPTY_FORM);
        setSelectedUser(null);
        setSelectedDesignation(null);
      }
      setErrors({});
      setShowCloseModal(false);
      setSelectedVendor(null);
      setUserSearch('');
      setUserResults([]);
      setRequestorDocFiles([]);
      setBulkFile(null);
      setBulkAnalysis(null);
      setBulkImportResult(null);
    }
  }, [duplicateFrom, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setProjectsLoading(true);
    api
      .get('/projects/list', { params: { page: 1, limit: 500, assigned_only: true } })
      .then((res) => {
        if (cancelled) return;
        const projects = res.data?.data?.projects || [];
        setProjectOptions(
          projects.map((p: any) => ({
            id: Number(p.id),
            label: p.project_short_name ? `${p.project_name} (${p.project_short_name})` : p.project_name,
            short_name: p.project_short_name || p.project_name,
          })),
        );
      })
      .catch(() => { if (!cancelled) setProjectOptions([]); })
      .finally(() => { if (!cancelled) setProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // A duplicated request may carry a project the user is no longer assigned to.
  useEffect(() => {
    if (projectsLoading || !formData.project_id) return;
    if (!projectOptions.some((p) => String(p.id) === formData.project_id)) {
      setFormData((prev) => ({ ...prev, project_id: '' }));
    }
  }, [projectOptions, projectsLoading, formData.project_id]);

  useEffect(() => {
    if (mode !== 'existing') {
      setUserResults([]);
      setUserSearchLoading(false);
      return;
    }

    const term = userSearch.trim();
    if (!/^\d{10}$/.test(term)) {
      setUserResults([]);
      setUserSearchLoading(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const params: Record<string, string | number> = { limit: 20, src: 'all', phone_number: term };
        const res = await api.get('/users/list', { params });
        if (!cancelled && res.data.success) {
          setUserResults(res.data.data.users || []);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setUserSearchLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mode, userSearch]);

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setUserResults([]);
    setFormData((prev) => ({
      ...prev,
      candidate_name: `${user.first_name} ${user.last_name}`.trim(),
      candidate_phone: user.phone_number || user.phone || '',
      candidate_location: user.location || '',
      candidate_designation: user.designation?.name || '',
      designation_id: user.designation?.id || null,
    }));

    if (user.designation) {
      setSelectedDesignation({ value: user.designation.id, label: user.designation.name });
    }
  };

  const fetchDesignations = useCallback(async (search = '') => {
    setDesignationLoading(true);
    try {
      const response = await api.get('/designations/list', {
        params: { search: search.trim() || undefined, limit: 100, status: 'active' },
      });
      if (response.data.success) {
        setDesignationOptions((response.data.data.designations || []).map((d: any) => ({ value: d.id, label: d.name })));
      }
    } finally {
      setDesignationLoading(false);
    }
  }, []);

  const fetchVendors = useCallback(async (search = '') => {
    setVendorLoading(true);
    try {
      const response = await api.get('/vendor/list', {
        params: { search: search.trim() || undefined, status: 'ACTIVE', limit: 50, page: 1 },
      });
      if (response.data.success) {
        const vendors = response.data.data.vendors || [];
        setVendorOptions(vendors.map((vendor: any) => ({
          value: vendor.id,
          label: vendor.vendor_full_name || vendor.vendor_short_name || `Vendor #${vendor.id}`,
        })));
      }
    } finally {
      setVendorLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchDesignations();
      fetchVendors();
    }
  }, [fetchDesignations, fetchVendors, isOpen]);

  const requestClose = useCallback(() => {
    if (loading || bulkAnalyzing || bulkImporting) return;
    setShowCloseModal(true);
  }, [loading, bulkAnalyzing, bulkImporting]);

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof RequestFormData, string>> = {};
    if (!formData.candidate_name.trim()) newErrors.candidate_name = 'Candidate name is required';
    if (!formData.candidate_phone.trim()) newErrors.candidate_phone = 'Phone is required';
    else if (!/^[0-9]{10}$/.test(formData.candidate_phone)) newErrors.candidate_phone = 'Must be 10 digits';
    if (!formData.candidate_location.trim()) newErrors.candidate_location = 'Location is required';
    if (!formData.designation_id) newErrors.candidate_designation = 'Designation is required';
    if (!formData.training_type_id) newErrors.training_type_id = 'Training type is required';
    if (!formData.project_id) newErrors.project_id = 'Project is required';
    if (formData.association_type === 'offrole' && !formData.vendor_id) newErrors.vendor_id = 'Vendor is required for vendor type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => {
      const updated = { ...prev, [name]: type === 'checkbox' ? checked : value } as RequestFormData;
      if (name === 'association_type' && value !== 'offrole') {
        setSelectedVendor(null);
        updated.vendor_id = '';
      }
      return updated;
    });
    if (errors[name as keyof RequestFormData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData: any = {
      candidate_name: formData.candidate_name,
      candidate_phone: formData.candidate_phone,
      candidate_location: formData.candidate_location,
      candidate_designation: formData.candidate_designation,
      designation_id: formData.designation_id,
      association_type: formData.association_type,
      training_type_id: Number(formData.training_type_id),
      project_id: Number(formData.project_id),
      deduction_applicable: formData.deduction_applicable,
    };

    if (selectedUser) submitData.user_id = selectedUser.id;
    if (formData.remarks.trim()) submitData.remarks = formData.remarks.trim();
    if (formData.association_type === 'offrole' && formData.vendor_id) submitData.vendor_id = Number(formData.vendor_id);
    if (formData.deduction_applicable) {
      if (formData.deduction_period) submitData.deduction_period = formData.deduction_period;
      if (formData.deduction_remarks.trim()) submitData.deduction_remarks = formData.deduction_remarks;
    }
    if (requestorDocFiles.length > 0) submitData._requestor_docs = requestorDocFiles;

    onSubmit(submitData);
  };

  const validateBulkFile = (file: File) => {
    const isValidType = VALID_BULK_FILE_TYPES.includes(file.type) || /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!isValidType) {
      toast.error('Please upload a valid CSV or Excel file');
      return false;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit');
      return false;
    }

    return true;
  };

  const handleBulkFileSelect = (file: File | null) => {
    if (!file) return;
    if (!validateBulkFile(file)) return;
    setBulkFile(file);
    setBulkAnalysis(null);
    setBulkImportResult(null);
  };

  const downloadSampleTemplate = () => {
    const headers = [
      'request_type',
      'employee_code',
      'candidate_name',
      'candidate_phone',
      'candidate_alternate_phone',
      'candidate_location',
      'candidate_designation',
      'association_type',
      'vendor_name',
      'training_type',
      'project_short_name',
      'remarks',
      'deduction_applicable',
      'deduction_period',
      'deduction_remarks',
    ];

    const rows = [
      {
        request_type: 'existing',
        employee_code: 'EMP001',
        candidate_name: '',
        candidate_phone: '',
        candidate_alternate_phone: '',
        candidate_location: 'Maharashtra',
        candidate_designation: '',
        association_type: 'inhouse',
        vendor_name: '',
        training_type: 'RF Survey',
        project_short_name: projectOptions[0]?.short_name || 'PROJECT-SHORT-NAME',
        remarks: 'Existing user batch',
        deduction_applicable: 'yes',
        deduction_period: 'leaving_time',
        deduction_remarks: 'Recover at exit',
      },
      {
        request_type: 'new',
        employee_code: '',
        candidate_name: 'Ravi Kumar',
        candidate_phone: '9876543210',
        candidate_alternate_phone: '',
        candidate_location: 'Karnataka',
        candidate_designation: 'Site Engineer',
        association_type: 'vendor',
        vendor_name: 'ABC Infra',
        training_type: 'RF Survey',
        project_short_name: projectOptions[0]?.short_name || 'PROJECT-SHORT-NAME',
        remarks: 'Fresh candidate',
        deduction_applicable: 'no',
        deduction_period: '',
        deduction_remarks: '',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    worksheet['!cols'] = headers.map((header) => ({ wch: Math.max(header.length, 18) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Training Requests');
    XLSX.writeFile(workbook, 'Eminence_Training_Request_Import_Template.xlsx');
  };

  const uploadBulkFile = async (endpoint: string) => {
    if (!bulkFile) {
      toast.error('Please select a file first');
      return null;
    }

    const formData = new FormData();
    formData.append('file', bulkFile);
    const response = await api.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.data;
  };

  const handleAnalyzeBulkFile = async () => {
    if (!bulkFile) {
      toast.error('Please select a file first');
      return;
    }

    setBulkAnalyzing(true);
    try {
      const data = await uploadBulkFile('/training/requests/bulk/analyze');
      if (data) {
        setBulkAnalysis(data);
        setBulkImportResult(null);
        toast.success('File analyzed successfully');
      }
    } catch (error: any) {
      console.error('Bulk analyze error:', error);
      toast.error(error?.response?.data?.message || 'Failed to analyze file');
    } finally {
      setBulkAnalyzing(false);
    }
  };

  const handleImportBulkFile = async () => {
    if (!bulkFile) {
      toast.error('Please select a file first');
      return;
    }

    setBulkImporting(true);
    try {
      const data = await uploadBulkFile('/training/requests/bulk/import');
      if (data) {
        setBulkImportResult(data);
        setBulkAnalysis(data.analysis || bulkAnalysis);
        toast.success(`Imported ${data.success_count} training request(s)`);
        await onBulkImportComplete?.();
      }
    } catch (error: any) {
      console.error('Bulk import error:', error);
      toast.error(error?.response?.data?.message || 'Failed to import file');
    } finally {
      setBulkImporting(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading && !bulkAnalyzing && !bulkImporting) {
        requestClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, bulkAnalyzing, bulkImporting, requestClose]);

  if (!isOpen) return null;

  const canSubmitSingle = loading || !formData.candidate_name.trim();
  const groupedBulkErrors = bulkAnalysis?.validation_errors?.slice(0, 12) || [];

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading && !bulkAnalyzing && !bulkImporting ? requestClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-3xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">New Training Request</h2>
            <p className="text-sm text-gray-600 mt-1">
              {duplicateFrom ? "Same user, new course. Select the course to create a duplicate request." : "Create one request or upload a CSV/Excel sheet for bulk requests."}
            </p>
          </div>
          <button onClick={requestClose} className="p-2 hover:bg-gray-200 rounded" disabled={loading || bulkAnalyzing || bulkImporting}>
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-5">
            <button
              type="button"
              onClick={() => { setMode('existing'); setSelectedUser(null); setFormData(EMPTY_FORM); setErrors({}); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${mode === 'existing' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <FaUser className="w-3.5 h-3.5" /> Existing User
            </button>
            <button
              type="button"
              onClick={() => { setMode('new'); setSelectedUser(null); setFormData(EMPTY_FORM); setErrors({}); setSelectedDesignation(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${mode === 'new' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <FaUserPlus className="w-3.5 h-3.5" /> New User
            </button>
            <button
              type="button"
              onClick={() => setMode('bulk')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${mode === 'bulk' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <FaFileCsv className="w-3.5 h-3.5" /> Bulk Upload
            </button>
          </div>

          {mode === 'bulk' ? (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Sheet rules</h3>
                <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                  <li>Use `request_type` as `existing` or `new`.</li>
                  <li>For existing users, `employee_code` must match exactly.</li>
                  <li>Use `training_type` and `vendor_name` labels, not database IDs.</li>
                  <li>
                    `project_short_name` is required and must be one of your assigned projects
                    {projectOptions.length > 0
                      ? `: ${projectOptions.map((p) => p.short_name).join(', ')}`
                      : ' (you have no assigned projects)'}
                    .
                  </li>
                  <li>No file is stored in S3 or kept for retry/history.</li>
                </ul>
                <button
                  type="button"
                  onClick={downloadSampleTemplate}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50"
                >
                  <FaFileCsv className="text-green-600" />
                  Download Sample File
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center bg-gray-50">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100">
                    <FaUpload className="w-8 h-8 text-blue-500" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Upload training request sheet</h3>
                <p className="text-sm text-gray-500 mb-4">CSV or Excel up to 5MB</p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Browse File
                  <input
                    type="file"
                    className="hidden"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={(e) => handleBulkFileSelect(e.target.files?.[0] || null)}
                    disabled={bulkAnalyzing || bulkImporting}
                  />
                </label>
              </div>

              {bulkFile && (
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <FaFileCsv className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{bulkFile.name}</p>
                      <p className="text-xs text-gray-500">{(bulkFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setBulkFile(null);
                      setBulkAnalysis(null);
                      setBulkImportResult(null);
                    }}
                    className="text-gray-400 hover:text-red-500"
                    disabled={bulkAnalyzing || bulkImporting}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}

              {bulkAnalysis && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                      <p className="text-xs font-medium text-blue-700">Total Rows</p>
                      <p className="mt-1 text-2xl font-semibold text-blue-900">{bulkAnalysis.total_rows}</p>
                    </div>
                    <div className="rounded-lg border border-green-100 bg-green-50 p-4">
                      <p className="text-xs font-medium text-green-700">Valid Rows</p>
                      <p className="mt-1 text-2xl font-semibold text-green-900">{bulkAnalysis.valid_rows}</p>
                    </div>
                    <div className="rounded-lg border border-red-100 bg-red-50 p-4">
                      <p className="text-xs font-medium text-red-700">Invalid Rows</p>
                      <p className="mt-1 text-2xl font-semibold text-red-900">{bulkAnalysis.invalid_rows}</p>
                    </div>
                    <div className="rounded-lg border border-purple-100 bg-purple-50 p-4">
                      <p className="text-xs font-medium text-purple-700">Existing / New</p>
                      <p className="mt-1 text-sm font-semibold text-purple-900">
                        {bulkAnalysis.mode_summary?.existing ?? 0} / {bulkAnalysis.mode_summary?.new ?? 0}
                      </p>
                    </div>
                  </div>

                  {groupedBulkErrors.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                      <h4 className="text-sm font-semibold text-red-900 flex items-center gap-2">
                        <FaExclamationTriangle />
                        Validation Errors
                      </h4>
                      <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                        {groupedBulkErrors.map((error, index) => (
                          <div key={`${error.row_number}-${index}`} className="rounded bg-white px-3 py-2 border border-red-100">
                            <p className="text-sm font-medium text-gray-900">Row {error.row_number} • {error.column}</p>
                            <p className="text-xs text-red-700 mt-0.5">{error.message}</p>
                          </div>
                        ))}
                        {bulkAnalysis.validation_errors.length > groupedBulkErrors.length && (
                          <p className="text-xs text-red-700">
                            Showing first {groupedBulkErrors.length} errors out of {bulkAnalysis.validation_errors.length}.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {bulkAnalysis.sample_data?.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900">Sample Data Preview</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              {Object.keys(bulkAnalysis.sample_data[0]).map((column) => (
                                <th key={column} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">{column}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {bulkAnalysis.sample_data.slice(0, 3).map((row, index) => (
                              <tr key={index} className="border-t border-gray-100">
                                {Object.keys(bulkAnalysis.sample_data[0]).map((column) => (
                                  <td key={column} className="px-3 py-2 whitespace-nowrap text-gray-700">{String(row[column] ?? '')}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {bulkImportResult && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-green-900 flex items-center gap-2">
                    <FaCheckCircle />
                    Import Result
                  </h4>
                  <p className="text-sm text-green-800">
                    {bulkImportResult.success_count} request(s) created, {bulkImportResult.failed_count} row(s) failed.
                  </p>
                  {bulkImportResult.created_requests.length > 0 && (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {bulkImportResult.created_requests.map((request) => (
                        <div key={request.id} className="rounded bg-white border border-green-100 px-3 py-2">
                          <p className="text-sm font-medium text-gray-900">{request.request_id}</p>
                          <p className="text-xs text-gray-600">{request.candidate_name} • {request.mode}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'existing' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search User</label>
                  {selectedUser ? (
                    <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm">
                      <div>
                        <span className="font-medium text-blue-800">{selectedUser.first_name} {selectedUser.last_name}</span>
                        {(selectedUser.employee_code || selectedUser.emp_code) && <span className="ml-2 text-blue-600 text-xs">({selectedUser.employee_code || selectedUser.emp_code})</span>}
                        <span className="ml-2 text-gray-500">{selectedUser.phone_number || selectedUser.phone}</span>
                      </div>
                      <button type="button" onClick={() => { setSelectedUser(null); setFormData(EMPTY_FORM); setSelectedDesignation(null); }} className="text-gray-400 hover:text-gray-600 ml-2">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Enter 10 digit phone number"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                        disabled={loading}
                      />
                      {userSearchLoading && (
                        <div className="absolute right-3 top-2.5"><FaSpinner className="w-4 h-4 animate-spin text-gray-400" /></div>
                      )}
                      {userResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-52 overflow-y-auto">
                          {userResults.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleSelectUser(user)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0"
                            >
                              <span className="font-medium">{user.first_name} {user.last_name}</span>
                              {(user.employee_code || user.emp_code) && <span className="ml-2 text-xs text-gray-500">Emp: {user.employee_code || user.emp_code}</span>}
                              {(user.phone_number || user.phone) && <span className="ml-2 text-xs text-gray-400">{user.phone_number || user.phone}</span>}
                              {user.designation && <span className="ml-2 text-xs text-blue-600">{user.designation.name}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Name *</label>
                  <input type="text" name="candidate_name" value={formData.candidate_name} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.candidate_name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="Full name" />
                  {errors.candidate_name && <p className="mt-1 text-sm text-red-600">{errors.candidate_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input type="text" name="candidate_phone" value={formData.candidate_phone} onChange={handleInputChange} disabled={loading} maxLength={10} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.candidate_phone ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="10 digit phone" />
                  {errors.candidate_phone && <p className="mt-1 text-sm text-red-600">{errors.candidate_phone}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trainee State *</label>
                  <input type="text" name="candidate_location" value={formData.candidate_location} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.candidate_location ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="Enter trainee state" />
                  {errors.candidate_location && <p className="mt-1 text-sm text-red-600">{errors.candidate_location}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                  <SearchableSelect
                    value={selectedDesignation}
                    onChange={(option) => {
                      setSelectedDesignation(option ? { value: Number(option.value), label: option.label } : null);
                      setFormData((prev) => ({
                        ...prev,
                        designation_id: option ? Number(option.value) : null,
                        candidate_designation: option?.label || '',
                      }));
                      if (errors.candidate_designation) setErrors((prev) => ({ ...prev, candidate_designation: '' }));
                    }}
                    options={designationOptions}
                    placeholder="Search and select designation"
                    disabled={loading}
                    isLoading={designationLoading}
                    onSearch={(query: string) => fetchDesignations(query)}
                    minSearchLength={3}
                    error={errors.candidate_designation}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Association Type *</label>
                  <select name="association_type" value={formData.association_type} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                    <option value="onrole">On-Role</option>
                    <option value="offrole">Off-Role</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                  <select name="training_type_id" value={formData.training_type_id} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white ${errors.training_type_id ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                    <option value="">Select course</option>
                    {trainingTypes.map((type) => <option key={type.id} value={type.id}>{type.training_name}</option>)}
                  </select>
                  {errors.training_type_id && <p className="mt-1 text-sm text-red-600">{errors.training_type_id}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
                <select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleInputChange}
                  disabled={loading || projectsLoading}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white ${errors.project_id ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                >
                  <option value="">{projectsLoading ? 'Loading projects...' : 'Select project'}</option>
                  {projectOptions.map((project) => (
                    <option key={project.id} value={project.id}>{project.label}</option>
                  ))}
                </select>
                {!projectsLoading && projectOptions.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">No projects are assigned to you, so a request can't be raised.</p>
                )}
                {errors.project_id && <p className="mt-1 text-sm text-red-600">{errors.project_id}</p>}
              </div>

              {formData.association_type === 'offrole' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <SearchableSelect
                    value={selectedVendor}
                    onChange={(option) => {
                      setSelectedVendor(option);
                      setFormData((prev) => ({ ...prev, vendor_id: option ? String(option.value) : '' }));
                      if (errors.vendor_id) setErrors((prev) => ({ ...prev, vendor_id: '' }));
                    }}
                    options={vendorOptions}
                    placeholder="Search and select vendor"
                    disabled={loading}
                    isLoading={vendorLoading}
                    onSearch={(query: string) => fetchVendors(query)}
                    minSearchLength={3}
                    error={errors.vendor_id}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea name="remarks" value={formData.remarks} onChange={handleInputChange} disabled={loading} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none" placeholder="Any additional remarks about this request" />
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <input type="checkbox" id="deduction_applicable" name="deduction_applicable" checked={formData.deduction_applicable} onChange={handleInputChange} className="w-4 h-4 text-blue-600 rounded" />
                  <label htmlFor="deduction_applicable" className="text-sm font-medium text-gray-700">Deduction Applicable</label>
                </div>
                {formData.deduction_applicable && (
                  <div className="space-y-4 pl-7">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Period</label>
                      <select name="deduction_period" value={formData.deduction_period} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                        <option value="training_time">At Training Time</option>
                        <option value="leaving_time">At Leaving Time</option>
                        <option value="two_months">Within Two Months</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Remarks</label>
                      <textarea name="deduction_remarks" value={formData.deduction_remarks} onChange={handleInputChange} disabled={loading} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none" placeholder="Remarks about deduction" />
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Requestor Documents (optional)</label>
                <label className="flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm border-gray-300 hover:border-blue-400 hover:bg-blue-50">
                  <FaUpload className="text-gray-400" />
                  <span className="text-gray-500">{requestorDocFiles.length > 0 ? `${requestorDocFiles.length} file(s) selected` : 'Upload documents'}</span>
                  <input type="file" multiple className="hidden" disabled={loading} onChange={(e) => setRequestorDocFiles(Array.from(e.target.files || []))} />
                </label>
              </div>
            </form>
          )}
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={requestClose} disabled={loading || bulkAnalyzing || bulkImporting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>

            {mode === 'bulk' ? (
              <>
                <button
                  type="button"
                  onClick={handleAnalyzeBulkFile}
                  disabled={!bulkFile || bulkAnalyzing || bulkImporting}
                  className="px-4 py-2 text-sm font-medium text-blue-800 bg-white border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2"
                >
                  {bulkAnalyzing ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileCsv className="w-4 h-4" />}
                  {bulkAnalyzing ? 'Analyzing...' : 'Analyze File'}
                </button>
                <button
                  type="button"
                  onClick={handleImportBulkFile}
                  disabled={!bulkFile || bulkImporting || bulkAnalyzing}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2"
                >
                  {bulkImporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
                  {bulkImporting ? 'Importing...' : 'Import Requests'}
                </button>
              </>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={canSubmitSingle} className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2">
                {loading ? (<><FaSpinner className="w-4 h-4 animate-spin" />Creating...</>) : (<><FaSave className="w-4 h-4" />Create Request</>)}
              </button>
            )}
          </div>
        </div>
      </div>

      <CloseModal isOpen={showCloseModal} onClose={() => setShowCloseModal(false)} onConfirm={confirmClose} />
    </>
  );
};
